import React from 'react';

import { createPortal } from 'react-dom';

interface FloatingTopSlotProps {
  visible: boolean;
  children: React.ReactNode;
}

const HOST_ID = 'floating-top-slots';

// One fixed column shared by every slot, so two notifications showing at once stack instead of
// covering each other. The strip spans the header, so only a visible card may take clicks.
const getHost = (): HTMLElement => {
  let host = document.getElementById(HOST_ID);
  if (!host) {
    host = document.createElement('div');
    host.id = HOST_ID;
    host.className = 'top-0 left-0 w-full z-50 fixed px-3 pt-3 flex flex-col items-center pointer-events-none';
    document.body.appendChild(host);
  }
  return host;
};

/**
 * Shared positioning + fade shell for top-center floating notifications
 * (passive toasts and actionable banners). Portaled to document.body so a
 * transformed ancestor can't shift the fixed layer; stays mounted and fades so
 * dismissal animates out instead of unmounting abruptly. A hidden slot has no
 * height, so it never pushes a visible one down.
 */
const FloatingTopSlot: React.FC<FloatingTopSlotProps> = ({ visible, children }) => {
  const visibilityClass = visible ? 'pb-3 opacity-100 translate-y-0 *:pointer-events-auto' : 'h-0 opacity-0 -translate-y-4';

  return createPortal(
    <div
      className={`w-full flex flex-col items-center transition-all duration-300 ease-out ${visibilityClass}`}
      data-testid="floating-top-slot">
      {children}
    </div>,
    getHost(),
  );
};

export default FloatingTopSlot;
